<script setup lang="ts">
import { onMounted, watch, computed } from "vue";
import dayjs from "dayjs";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import {
  useChangelog,
  groupItemsByCategory,
  groupReleasesByMonth,
  type ChangelogCategory,
} from "@/composables/api/useChangelog";

/**
 * The "What's new" feed on the dashboard. Reads `changelog.json`, published
 * straight to the app's S3 bucket by the `changelog` skill — so this list can
 * change without a deploy.
 *
 * Laid out as a timeline: the month sits once in a left margin, the release
 * date once above that release's groups, and the category once in the gutter
 * beside its box. Nothing that identifies a group repeats down the page —
 * earlier versions carried a category chip and an area on all eleven rows,
 * which gave every headline a different left edge and said "August" twelve
 * times.
 */
const { releases, isLoading, isError, newReleaseIds, markAllSeen } =
  useChangelog();

// The heading opens something that is not the changelog. This component emits and stays
// ignorant of what listens — the dashboard decides. That keeps the game out of this file's
// tests, and makes "a different thing behind the same trigger" a change in one other place.
const emit = defineEmits<{ titleClick: [] }>();

const months = computed(() => groupReleasesByMonth(releases.value));

const CATEGORY_LABEL: Record<ChangelogCategory, string> = {
  new: "New",
  improved: "Improved",
  fixed: "Fixed",
};

/**
 * Whole class strings, not composed fragments — Tailwind only ships classes it
 * can see written out. Deliberately not the emerald/blue/amber of the first
 * version, which read as louder than the rest of the page.
 */
const CATEGORY_COLOR: Record<ChangelogCategory, string> = {
  new: "text-violet-700 dark:text-violet-300",
  improved: "text-teal-700 dark:text-teal-300",
  fixed: "text-orange-700 dark:text-orange-300",
};

function formatMonth(date: string): { month: string; year: string } {
  const parsed = dayjs(date);
  return parsed.isValid()
    ? { month: parsed.format("MMMM"), year: parsed.format("YYYY") }
    : { month: date, year: "" };
}

function formatReleaseDate(date: string): string {
  const parsed = dayjs(date);
  return parsed.isValid() ? parsed.format("MM/DD/YY") : date;
}

// Mark seen only once there is something on screen to have been seen. Watched
// as well as mounted because the data usually lands after the first paint.
onMounted(markAllSeen);
watch(releases, (value) => {
  if (value.length > 0) {
    markAllSeen();
  }
});
</script>

<template>
  <section>
    <!--
      The heading sits above the card rather than inside it: it titles this
      section of the dashboard, not the card's contents, and at this size it
      would crowd the card header. It is also about to become the trigger for
      something that isn't the changelog, so belonging to the card would be
      actively misleading.
    -->
    <!--
      cursor-pointer is the ONLY affordance, by design. No colour change, no hover style, no
      title attribute, and deliberately no role="button" or tabindex — those announce the easter
      egg to screen readers and keyboard users, which is exactly the discoverability the spec
      rules out. Double-clicking to select the text will also fire it; that is accepted.
    -->
    <h2
      class="mb-4 cursor-pointer text-2xl font-semibold tracking-tight"
      data-testid="changelog-heading"
      @click="emit('titleClick')"
    >
      What's new
    </h2>

    <Card>
      <CardContent>
        <div v-if="isLoading" class="space-y-3" data-testid="changelog-loading">
          <Skeleton class="h-4 w-32" />
          <Skeleton class="h-4 w-full" />
          <Skeleton class="h-4 w-3/4" />
        </div>

        <p
          v-else-if="isError"
          class="text-sm text-muted-foreground"
          data-testid="changelog-error"
        >
          The changelog couldn't be loaded right now.
        </p>

        <p
          v-else-if="releases.length === 0"
          class="text-sm text-muted-foreground"
          data-testid="changelog-empty"
        >
          No updates have been published yet.
        </p>

        <div v-else class="space-y-8" data-testid="changelog-releases">
          <section
            v-for="month in months"
            :key="month.key"
            class="grid grid-cols-1 gap-x-4 gap-y-3 sm:grid-cols-[92px_minmax(0,1fr)]"
            data-testid="changelog-month"
          >
            <div class="flex gap-3">
              <div
                class="w-[3px] shrink-0 rounded-full bg-violet-500"
                aria-hidden="true"
              ></div>
              <!--
                Row on mobile, column in the gutter from sm up. A <br> would
                have needed hiding at one breakpoint, which browsers handle
                inconsistently; flex-direction says the same thing plainly.
              -->
              <h3
                class="flex flex-wrap items-baseline gap-x-1.5 text-lg font-medium leading-tight sm:flex-col sm:gap-x-0"
              >
                <span>{{ formatMonth(month.date).month }}</span>
                <span>{{ formatMonth(month.date).year }}</span>
              </h3>
            </div>
            <div class="hidden sm:block"></div>

            <template v-for="release in month.releases" :key="release.id">
              <div class="hidden sm:block"></div>
              <div
                class="flex items-center gap-2.5"
                data-testid="changelog-release-date"
              >
                <span class="text-sm font-medium">{{
                  formatReleaseDate(release.date)
                }}</span>
                <span
                  v-if="newReleaseIds.has(release.id)"
                  class="rounded-full bg-primary px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-primary-foreground"
                  data-testid="changelog-new-badge"
                >
                  New
                </span>
              </div>

              <template
                v-for="group in groupItemsByCategory(release.items)"
                :key="`${release.id}-${group.category}`"
              >
                <div
                  class="text-sm font-medium sm:pt-3 sm:text-right"
                  :class="CATEGORY_COLOR[group.category]"
                  data-testid="changelog-category"
                >
                  {{ CATEGORY_LABEL[group.category] }}
                </div>

                <div
                  class="rounded-xl bg-muted/60 p-2.5"
                  data-testid="changelog-group"
                >
                  <article
                    v-for="(item, index) in group.items"
                    :key="`${group.category}-${index}`"
                    class="mb-1.5 rounded-lg border bg-card px-3 py-2.5 last:mb-0"
                  >
                    <span
                      v-if="item.area"
                      class="inline-block rounded-full border bg-muted px-2.5 py-0.5 text-[11px] font-medium text-muted-foreground"
                      data-testid="changelog-area"
                    >
                      {{ item.area }}
                    </span>
                    <p class="mt-1.5 text-sm font-medium leading-snug">
                      {{ item.headline }}
                    </p>
                    <p
                      v-if="item.detail"
                      class="mt-1 text-sm leading-snug text-muted-foreground"
                    >
                      {{ item.detail }}
                    </p>
                  </article>
                </div>
              </template>
            </template>
          </section>
        </div>
      </CardContent>
    </Card>
  </section>
</template>
